import React, { BaseSyntheticEvent, ChangeEvent, useEffect } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Radio,
    RadioGroupProps,
    RadioGroup as MUIRadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import {
    GenericObject,
    ScreenProps,
    checkEmail,
    checkPhoneNumber,
    common,
    getApplicationId,
    getIsCallCenter,
    getIsPortal,
    getIsSalesForce,
    getIsWebsite,
} from '@fnol/library/src/lib';
import {
    InfoText,
    PhoneInputField,
    StepWrapper,
    StepperControls,
    StyledCardSection,
    YesNoSelector,
} from '@fnol/library/src/components';
import { getUserData } from '../../rpc/actions';
import { personType, questionMaxLength, riskTypeLife, submitterRole, translations } from '../../lib/consts';
import { useValidateIndividual } from '../../lib/customHooks';
import Individual, { IndividualCleanup } from '../Individual/Individual';
import AMLSection, { AMLSectionCleanup } from '../AMLSection/AMLSection';

interface FormData {
    submitterRole: string;
    submitterName: string;
    submitterLastName: string;
    isSubmitterUser: string;
    submitterEmail: string;
    submitterPhone: string;
    agreementContract: string;
    agreementDocumentation: string;
    agreementSMS: string;
    agreementEmail: string;
    risks: number[];
    agreementData: string;
    agreementDataForContract: boolean;
    agreementObligation: boolean;
    isSubmitterUsersAttorney: string;
    submitterAttorneySignature: string;
}

const SubmitterDetailScreen = ({ navigator }: ScreenProps) => {
    const [currentStep, { goForward, goBack }] = navigator;
    const { values, setFieldValue, errors, setErrors, touched, setTouched } = useFormikContext<FormData>();

    const submitterRoleVisible = getIsWebsite(getApplicationId()) || getIsCallCenter(getApplicationId());
    const submitterValidation = useValidateIndividual(personType.submitter);

    const editIds = [1, 2, 3, 4, 5];

    const purchaseSubmitterRoleContractor = !!(
        getIsSalesForce(getApplicationId()) &&
        values.risks.includes(riskTypeLife.purchase) &&
        values.submitterRole === submitterRole.contractor
    );
    const purchaseSubmitterRoleAttorney = !!(
        getIsSalesForce(getApplicationId()) &&
        values.risks.includes(riskTypeLife.purchase) &&
        values.submitterRole === submitterRole.attorney
    );

    const includesDeath = () => {
        return values?.risks.some((risk) =>
            [riskTypeLife.death, riskTypeLife.deathByAccident, riskTypeLife.deathByTrafficAccident].includes(risk),
        );
    };

    const RadioGroup = (params: RadioGroupProps) => {
        return (
            <>
                <MUIRadioGroup {...params}>
                    <FormControlLabel
                        value={submitterRole.contractor}
                        control={<Radio />}
                        label={translations.personalInfo.PURCHASE_SUBMITTER_CONTRACTOR}
                    />
                    <FormControlLabel
                        value={submitterRole.attorney}
                        control={<Radio />}
                        label={translations.personalInfo.PURCHASE_SUBMITTER_ATTORNEY}
                    />
                </MUIRadioGroup>
            </>
        );
    };

    const handleGoBack = (): void => {
        goBack();
    };

    const handleNext = () => {
        setTouched(
            {
                submitterRole: submitterRoleVisible,
                submitterName: true,
                submitterLastName: true,
                isSubmitterUser: true,
                submitterEmail: true,
                submitterPhone: true,
                agreementDataForContract: true,
                agreementObligation: true,
                isSubmitterUsersAttorney: true,
                ...submitterValidation.touched,
            },
            false,
        );
        let existingErrors: GenericObject = {};
        let valid = true;

        if (submitterRoleVisible) {
            if (!values.submitterRole) {
                existingErrors.submitterRole = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
        }
        if (!values.risks.includes(riskTypeLife.purchase) || purchaseSubmitterRoleAttorney) {
            if (values.submitterName === '') {
                existingErrors.submitterName = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (values.submitterLastName === '') {
                existingErrors.submitterLastName = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
        }

        if (purchaseSubmitterRoleAttorney && !values.submitterAttorneySignature) {
            existingErrors.submitterAttorneySignature = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (!values.risks.includes(riskTypeLife.purchase)) {
            if (!values.isSubmitterUser) {
                existingErrors.isSubmitterUser = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (
                !values.risks.includes(riskTypeLife.purchase) &&
                values.isSubmitterUser === 'false' &&
                getIsSalesForce(getApplicationId()) &&
                !values.isSubmitterUsersAttorney
            ) {
                existingErrors.isSubmitterUsersAttorney = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (
                !includesDeath() &&
                !values.risks.includes(riskTypeLife.outlive) &&
                values.agreementDataForContract !== true
            ) {
                existingErrors.agreementDataForContract = translations.validationMessages.DATA_AGREEMENT;
                valid = false;
            }
            if (
                !includesDeath() &&
                !values.risks.includes(riskTypeLife.outlive) &&
                values.agreementObligation !== true
            ) {
                existingErrors.agreementObligation = translations.validationMessages.DATA_AGREEMENT;
                valid = false;
            }
        }

        if (
            (values.isSubmitterUser && !values.risks.includes(riskTypeLife.purchase)) ||
            purchaseSubmitterRoleAttorney
        ) {
            if (values.submitterEmail === '') {
                existingErrors.submitterEmail = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            } else if (!checkEmail(values.submitterEmail)) {
                existingErrors.submitterEmail = translations.validationMessages.EMAIL_ADDRESS_FORMAT;
                valid = false;
            }
            if (values.submitterPhone.length <= 4) {
                existingErrors.submitterPhone = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            } else if (!checkPhoneNumber(values.submitterPhone)) {
                existingErrors.submitterPhone = translations.validationMessages.PHONE_NUMBER_FORMAT;
                valid = false;
            }
        }

        if (
            values.isSubmitterUser === 'true' &&
            !(getIsSalesForce(getApplicationId()) && values.risks.includes(riskTypeLife.outlive))
        ) {
            if (!submitterValidation.isValid) valid = false;
        }

        existingErrors = {
            ...existingErrors,
            ...submitterValidation.errors,
        };

        setErrors(existingErrors);

        if (valid || purchaseSubmitterRoleContractor) goForward();
    };

    const handleChangeIsSubmitterUser = () => {
        setFieldValue('paymentToUser', '');
        setFieldValue('isSubmitterUsersAttorney', '');
        editIds.map((editId: number) => {
            if (!`user${editId}Name`) {
                setFieldValue(`paymentToUser${editId}`, '');
            }
        });
        IndividualCleanup(personType.submitter, setFieldValue);
        AMLSectionCleanup(personType.submitter, setFieldValue);
        IndividualCleanup(personType.user, setFieldValue);
        AMLSectionCleanup(personType.user, setFieldValue);
    };

    const handleChangeIsSubmitterUsersAttorney = () => {
        setFieldValue('paymentToUser', '');
        editIds.map((editId: number) => {
            if (!`user${editId}Name`) {
                setFieldValue(`paymentToUser${editId}`, '');
            }
        });
        IndividualCleanup(personType.submitter, setFieldValue);
        AMLSectionCleanup(personType.submitter, setFieldValue);
        IndividualCleanup(personType.user, setFieldValue);
        AMLSectionCleanup(personType.user, setFieldValue);
    };

    const handleChangeSubmitterRole = (event: ChangeEvent<HTMLInputElement>) => {
        setFieldValue('submitterRole', event.target.value);
        setFieldValue('submitterName', '');
        setFieldValue('submitterLastName', '');
        setFieldValue('submitterPhone', '');
        setFieldValue('submitterEmail', '');
        AMLSectionCleanup(personType.submitter, setFieldValue);
    };

    const onCheckBoxChangeDataForContract = (e: BaseSyntheticEvent) => {
        setFieldValue('agreementDataForContract', e.target.checked);
    };

    const onCheckBoxChangeAgreementObligation = (e: BaseSyntheticEvent) => {
        setFieldValue('agreementObligation', e.target.checked);
    };

    useEffect(() => {
        if (getIsPortal(getApplicationId())) {
            getUserData().then((response) => {
                response.data.ime && setFieldValue('submitterName', response.data.ime);
                response.data.prezime && setFieldValue('submitterLastName', response.data.prezime);
                response.data.email && setFieldValue('submitterEmail', response.data.email);
                response.data.telefon && setFieldValue('submitterPhone', response.data.telefon);
                setFieldValue('submitterRole', translations.personalInfo.DEFAULT_SUBMITTER); // default value for portal
            });
        }
    }, []);

    return (
        <StepWrapper>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                    {translations.CLAIM_SUBMITTER}
                </Typography>

                {values.risks.includes(riskTypeLife.purchase) && (
                    <>
                        <InfoText
                            label={
                                <>
                                    <span>{translations.personalInfo.PURCHASE_SUBMITTER_INFO_1}</span>{' '}
                                    <span style={{ fontWeight: 'bold' }}>
                                        {translations.personalInfo.PURCHASE_SUBMITTER_INFO_2}
                                    </span>
                                </>
                            }
                        ></InfoText>

                        <FormControl>
                            <Typography variant="subtitle1">
                                {translations.personalInfo.SUBMITTER_ROLE_PURCHASE}
                            </Typography>

                            <Field
                                type="boolean"
                                name="submitterRole"
                                as={RadioGroup}
                                onChange={handleChangeSubmitterRole}
                            />
                            <FormHelperText error={true}>
                                <>{errors.submitterRole}</>
                            </FormHelperText>
                        </FormControl>
                    </>
                )}

                {submitterRoleVisible && !values.risks.includes(riskTypeLife.purchase) ? (
                    <FormControl required={true}>
                        <Field
                            name="submitterRole"
                            label={translations.personalInfo.SUBMITTER_ROLE}
                            as={TextField}
                            inputProps={{
                                maxLength: questionMaxLength,
                            }}
                            error={!!(touched.submitterRole && errors.submitterRole)}
                            required={true}
                        />
                        {touched.submitterRole && errors.submitterRole ? (
                            <FormHelperText error={true}>
                                <>{errors.submitterRole}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                ) : null}

                {(!values.risks.includes(riskTypeLife.purchase) || purchaseSubmitterRoleAttorney) && (
                    <>
                        <FormControl>
                            <Field
                                name="submitterName"
                                label={translations.personalInfo.NAME}
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.submitterName && errors.submitterName)}
                                required={true}
                            />
                            {touched.submitterName && errors.submitterName ? (
                                <FormHelperText error={true}>
                                    <>{errors.submitterName}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>

                        <FormControl>
                            <Field
                                name="submitterLastName"
                                label={translations.personalInfo.LAST_NAME}
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.submitterLastName && errors.submitterLastName)}
                                required={true}
                            />
                            {touched.submitterLastName && errors.submitterLastName ? (
                                <FormHelperText error={true}>
                                    <>{errors.submitterLastName}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                )}

                {!values.risks.includes(riskTypeLife.purchase) && (
                    <>
                        <Typography variant="subtitle1">{translations.policyUser.USER_BY_POLICY}</Typography>
                        <FormControl>
                            <YesNoSelector name="isSubmitterUser" onChange={() => handleChangeIsSubmitterUser()} />
                        </FormControl>

                        {values.isSubmitterUser === 'false' && (
                            <>
                                {getIsSalesForce(getApplicationId()) && (
                                    <>
                                        <Typography variant="subtitle1">
                                            {translations.policyUser.SUBMITTER_USERS_ATTORNEY}
                                        </Typography>
                                        <FormControl>
                                            <YesNoSelector
                                                name="isSubmitterUsersAttorney"
                                                onChange={() => handleChangeIsSubmitterUsersAttorney()}
                                            />
                                        </FormControl>

                                        {values.isSubmitterUsersAttorney === 'true' && (
                                            <AMLSection type={personType.submitter} userAttorney={true}></AMLSection>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}

                {((values.isSubmitterUser && !values.risks.includes(riskTypeLife.purchase)) ||
                    purchaseSubmitterRoleAttorney) && (
                    <>
                        <FormControl>
                            <PhoneInputField
                                name="submitterPhone"
                                specialLabel={translations.personalInfo.MOBILE_STAR}
                                country={'rs'}
                                required={true}
                            />
                        </FormControl>

                        <FormControl>
                            <Field
                                name="submitterEmail"
                                label={translations.personalInfo.EMAIL}
                                type="email"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.submitterEmail && errors.submitterEmail)}
                                required={true}
                            />
                            {touched.submitterEmail && errors.submitterEmail ? (
                                <FormHelperText error={true}>
                                    <>{errors.submitterEmail}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                )}

                {purchaseSubmitterRoleAttorney && (
                    <AMLSection type={personType.submitter} userAttorney={true}></AMLSection>
                )}

                {values.isSubmitterUser === 'true' && !values.risks.includes(riskTypeLife.purchase) && (
                    <>
                        {!(getIsSalesForce(getApplicationId()) && values.risks.includes(riskTypeLife.outlive)) && (
                            <>
                                <Individual type={personType.submitter}></Individual>
                                {getIsSalesForce(getApplicationId()) && (
                                    <AMLSection type={personType.submitter}></AMLSection>
                                )}
                            </>
                        )}

                        <Typography variant="subtitle1">{translations.personalInfo.AGREEMENT_1}</Typography>
                        <FormControl>
                            <YesNoSelector name="agreementContract" />
                        </FormControl>
                        <Typography variant="subtitle1">{translations.personalInfo.AGREEMENT_2}</Typography>
                        <FormControl>
                            <YesNoSelector name="agreementDocumentation" />
                        </FormControl>
                        <Typography variant="subtitle1">{translations.personalInfo.AGREEMENT_3}</Typography>
                        <FormControl>
                            <YesNoSelector name="agreementSMS" />
                        </FormControl>
                        <Typography variant="subtitle1">{translations.personalInfo.AGREEMENT_4}</Typography>
                        <FormControl>
                            <YesNoSelector name="agreementEmail" />
                        </FormControl>
                    </>
                )}
                {includesDeath() ? (
                    <>
                        <Typography sx={{ fontWeight: 'bold', margin: 0 }} gutterBottom={true}>
                            {translations.personalInfo.PAYMENT_REQUEST}
                        </Typography>
                        <Typography variant="subtitle1">{translations.personalInfo.AGREEMENT_5}</Typography>
                        <FormControl>
                            <YesNoSelector name="agreementData" />
                        </FormControl>
                    </>
                ) : (
                    <>
                        {!values.risks.includes(riskTypeLife.outlive) &&
                            !values.risks.includes(riskTypeLife.purchase) && (
                                <>
                                    <Typography sx={{ fontWeight: 'bold', margin: 0 }} gutterBottom={true}>
                                        {translations.personalInfo.PAYMENT_REQUEST}
                                    </Typography>
                                    <FormControlLabel
                                        name="agreementDataForContract"
                                        control={<Checkbox />}
                                        onChange={onCheckBoxChangeDataForContract}
                                        label={translations.personalInfo.AGREEMENT_6}
                                        checked={values.agreementDataForContract}
                                    />
                                    {touched.agreementDataForContract && errors.agreementDataForContract ? (
                                        <FormHelperText error={true}>
                                            <>{errors.agreementDataForContract}</>
                                        </FormHelperText>
                                    ) : null}

                                    <FormControlLabel
                                        name="agreementObligation"
                                        control={<Checkbox />}
                                        onChange={onCheckBoxChangeAgreementObligation}
                                        label={translations.personalInfo.AGREEMENT_7}
                                        checked={values.agreementObligation}
                                    />
                                    {touched.agreementObligation && errors.agreementObligation ? (
                                        <FormHelperText error={true}>
                                            <>{errors.agreementObligation}</>
                                        </FormHelperText>
                                    ) : null}
                                </>
                            )}
                    </>
                )}
            </StyledCardSection>
            <StepperControls
                onPrevious={handleGoBack}
                onPreviousLabel={common.PREVIOUS}
                onNext={handleNext}
                onNextLabel={common.NEXT}
            />
        </StepWrapper>
    );
};

export default SubmitterDetailScreen;
